import { format, formatDistanceToNow, isValid } from 'date-fns';

/**
 * Format currency amount
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency code (default: USD)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD') => {
  if (typeof amount !== 'number' || isNaN(amount)) {
    return '$0.00';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount);
};

/**
 * Format date for display
 * @param {Date|string} date - The date to format
 * @param {string} formatString - Date format string (default: 'MMM dd, yyyy')
 * @returns {string} Formatted date string
 */
export const formatDate = (date, formatString = 'MMM dd, yyyy') => {
  if (!date) return 'N/A';
  
  const dateObj = new Date(date);
  if (!isValid(dateObj)) return 'Invalid Date';
  
  return format(dateObj, formatString);
};

/**
 * Format date and time for display
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date) => {
  return formatDate(date, 'MMM dd, yyyy h:mm a');
};

/**
 * Format relative time (e.g., "2 hours ago")
 * @param {Date|string} date - The date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return 'N/A';
  
  const dateObj = new Date(date);
  if (!isValid(dateObj)) return 'Invalid Date';
  
  return formatDistanceToNow(dateObj, { addSuffix: true });
};

/**
 * Format phone number
 * @param {string} phone - The phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  const cleaned = phone.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  
  return phone;
};

/**
 * Truncate text to specified length
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length (default: 50)
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  
  if (text.length <= maxLength) return text;
  
  return text.substring(0, maxLength) + '...';
};

/**
 * Format file size in bytes to human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format number with commas
 * @param {number} number - The number to format
 * @returns {string} Formatted number
 */
export const formatNumber = (number) => {
  if (typeof number !== 'number' || isNaN(number)) {
    return '0';
  }
  
  return new Intl.NumberFormat('en-US').format(number);
};

/**
 * Format percentage
 * @param {number} value - The value to format as percentage
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, decimals = 1) => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '0%';
  }
  
  return `${value.toFixed(decimals)}%`;
};

/**
 * Capitalize first letter of each word
 * @param {string} text - The text to capitalize
 * @returns {string} Capitalized text
 */
export const capitalizeWords = (text) => {
  if (!text) return '';
  
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Format status text
 * @param {string} status - The status to format
 * @returns {string} Formatted status text
 */
export const formatStatus = (status) => {
  const statusMap = {
    pending: 'Pending',
    pending_approval: 'Pending Approval',
    paid: 'Paid',
    failed: 'Failed',
    expired: 'Expired'
  };
  
  return statusMap[status] || capitalizeWords(status);
};

/**
 * Get status color class
 * @param {string} status - The status
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @returns {string} CSS class name
 */
export const getStatusColorClass = (status, isDarkMode = false) => {
  if (isDarkMode) {
    const darkColorMap = {
      pending: 'badge-warning-dark',
      pending_approval: 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-900/40 text-orange-300 border border-orange-700',
      paid: 'badge-success-dark',
      failed: 'badge-danger-dark',
      expired: 'badge-expired-dark'
    };
    return darkColorMap[status] || 'badge-expired-dark';
  }
  
  const colorMap = {
    pending: 'badge-warning',
    pending_approval: 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800 border border-orange-300',
    paid: 'badge-success',
    failed: 'badge-danger',
    expired: 'badge-expired'
  };
  
  return colorMap[status] || 'badge-expired';
};

/**
 * Calculate time until expiration
 * @param {Date|string} expiresAt - The expiration date
 * @returns {object} Time remaining object
 */
export const getTimeUntilExpiration = (expiresAt) => {
  if (!expiresAt) return { expired: true, hours: 0, minutes: 0, seconds: 0 };
  
  const now = new Date();
  const expiration = new Date(expiresAt);
  const diff = expiration.getTime() - now.getTime();
  
  if (diff <= 0) {
    return { expired: true, hours: 0, minutes: 0, seconds: 0 };
  }
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  return {
    expired: false,
    hours,
    minutes,
    seconds,
    totalMinutes: Math.floor(diff / (1000 * 60))
  };
};
